export interface PickupPricing {
  pathankot: number;
  amritsar: number;
  chandigarh: number;
  delhiNCR: number;
}

export interface DayItinerary {
  day: number;
  title: string;
  description: string;
  distance?: string;
  travelTime?: string;
  sightseeing?: string[];
  meals?: string;
  overnight?: string;
  notes?: string;
}

export interface TourPackage {
  id: string;
  name: string;
  destination: string;
  image: string;
  alt: string;
  duration: string;
  durationDays: number;
  durationNights: number;
  rating: number;
  reviews: number;
  price: number;
  originalPrice: number;
  discount: number;
  category: string;
  travelType: string[];
  includes: string[];
  excludes: string[];
  highlights: string[];
  tag: string;
  tagColor: string;
  season: string[];
  bestTimeToVisit: string;
  budgetTier: 'budget' | 'mid' | 'premium' | 'luxury';
  description: string;
  pickupPricing: PickupPricing;
  itinerary: DayItinerary[];
  vehicleDetails: string;
  hotelDetails?: string;
  bookingPolicy: string;
  cancellationPolicy: string;
  faqs: {q: string;a: string;}[];
}

export const ALL_PACKAGES: TourPackage[] = [
// ─── MANIMAHESH YATRA ───────────────────────────────────────────────────────
{
  id: 'pkg-manimahesh-yatra-4d',
  name: 'Manimahesh Yatra Package',
  destination: 'Pathankot → Bharmour → Manimahesh Lake, Chamba HP',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_120783796-1778666105784.png",
  alt: 'Manimahesh Lake with snow-capped Kailash peak reflection in crystal clear water Chamba Himachal Pradesh',
  duration: '4D / 3N',
  durationDays: 4,
  durationNights: 3,
  rating: 4.9,
  reviews: 312,
  price: 8999,
  originalPrice: 12000,
  discount: 25,
  category: 'Manimahesh Yatra',
  travelType: ['Pilgrimage', 'Family', 'Group'],
  includes: ['Vehicle (Hatchback/SUV)', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel/Accommodation', 'Meals', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Pony/Porter Charges', 'Travel Insurance'],
  highlights: ['Manimahesh Lake', 'Bharmour Chaurasi Temple', 'Kailash Peak View', 'Hadsar Base Camp', 'Sacred Pilgrimage Trek'],
  tag: 'Pilgrimage Special',
  tagColor: 'badge-accent',
  season: ['Summer', 'Autumn'],
  bestTimeToVisit: 'August – September (Yatra Season)',
  budgetTier: 'budget',
  description: 'Embark on the sacred Manimahesh Yatra from Pathankot to the holy Manimahesh Lake at 4,080m altitude. Visit the ancient Bharmour temples and seek blessings of Lord Shiva at this divine pilgrimage.',
  pickupPricing: { pathankot: 8999, amritsar: 10999, chandigarh: 13999, delhiNCR: 18999 },
  itinerary: [
  { day: 1, title: 'Pathankot to Bharmour', description: 'Depart from Pathankot early morning. Drive via Dalhousie and Chamba to Bharmour (220 km). Check in at dharamshala/hotel. Visit Chaurasi Temple complex in the evening.', distance: '220 km', travelTime: '7–8 hours', sightseeing: ['Chamba Town', 'Chaurasi Temple Complex'], meals: 'Dinner (self)', overnight: 'Bharmour' },
  { day: 2, title: 'Bharmour to Hadsar – Trek to Manimahesh Lake', description: 'Drive from Bharmour to Hadsar (14 km). Begin the 13 km trek to Manimahesh Lake. Take a holy dip in the sacred lake. Witness the magnificent Kailash Peak.', distance: '14 km drive + 13 km trek', travelTime: '5–6 hours trek', sightseeing: ['Hadsar Village', 'Manimahesh Lake', 'Kailash Peak'], meals: 'Self-arranged', overnight: 'Manimahesh / Hadsar', notes: 'Carry warm clothes, trekking shoes, and personal medicines.' },
  { day: 3, title: 'Return Trek to Hadsar – Drive to Bharmour', description: 'Return trek from Manimahesh Lake to Hadsar. Drive back to Bharmour. Rest and explore local market.', distance: '13 km trek + 14 km drive', travelTime: '4–5 hours', sightseeing: ['Bharmour Market', 'Local Temples'], meals: 'Self-arranged', overnight: 'Bharmour' },
  { day: 4, title: 'Bharmour to Pathankot – Departure', description: 'After breakfast, depart from Bharmour. Drive back to Pathankot via Chamba. Trip ends at Pathankot.', distance: '220 km', travelTime: '7–8 hours', meals: 'Breakfast (self)', overnight: 'Drop at Pathankot' }],

  vehicleDetails: 'Hatchback (up to 4 pax) / Sedan (up to 4 pax) / SUV Ertiga (up to 6 pax) / Innova (up to 7 pax). All vehicles are AC, well-maintained with experienced hill drivers.',
  hotelDetails: 'Accommodation not included. Dharamshalas and budget hotels available at Bharmour and Hadsar.',
  bookingPolicy: 'Minimum 20% advance payment required to confirm booking. Balance payable before departure.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is the trek difficult?', a: 'The 13 km trek is moderate. Pony and porter services are available at Hadsar for those who need assistance.' },
  { q: 'What is the best time for Manimahesh Yatra?', a: 'The official Yatra season is August–September. The lake is accessible from July to October.' },
  { q: 'Is accommodation included?', a: 'No, accommodation is not included. Dharamshalas are available at Bharmour and Hadsar at nominal charges.' }]

},

// ─── CHAMBA TOURS ────────────────────────────────────────────────────────────
{
  id: 'pkg-chamba-heritage-3d',
  name: 'Chamba Heritage & Temple Tour',
  destination: 'Pathankot → Chamba → Khajjiar, Himachal Pradesh',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1752e6a6b-1786025619690.png",
  alt: 'Chamba town with ancient Lakshmi Narayan temples and Ravi river valley Himachal Pradesh',
  duration: '3D / 2N',
  durationDays: 3,
  durationNights: 2,
  rating: 4.6,
  reviews: 187,
  price: 7499,
  originalPrice: 9500,
  discount: 21,
  category: 'Chamba',
  travelType: ['Family', 'Pilgrimage', 'Couple'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Lakshmi Narayan Temple', 'Chamba Chaugan', 'Khajjiar Lake', 'Akhand Chandi Palace', 'Bhuri Singh Museum'],
  tag: 'Heritage',
  tagColor: 'badge-accent',
  season: ['Spring', 'Summer', 'Autumn'],
  bestTimeToVisit: 'March – June, September – November',
  budgetTier: 'budget',
  description: 'Explore the ancient royal town of Chamba with its magnificent temples, scenic Chaugan ground, and the mini Switzerland of India — Khajjiar. A perfect blend of heritage, spirituality, and nature.',
  pickupPricing: { pathankot: 7499, amritsar: 9499, chandigarh: 12499, delhiNCR: 17499 },
  itinerary: [
  { day: 1, title: 'Pathankot to Chamba', description: 'Depart from Pathankot. Drive through scenic Ravi valley to Chamba (120 km). Check in at hotel. Evening visit to Chaugan ground and local market.', distance: '120 km', travelTime: '4–5 hours', sightseeing: ['Chamba Chaugan', 'Local Market'], overnight: 'Chamba' },
  { day: 2, title: 'Chamba Sightseeing & Khajjiar', description: 'Morning visit to Lakshmi Narayan Temple complex and Bhuri Singh Museum. Drive to Khajjiar (24 km) — the mini Switzerland of India. Enjoy the meadow and lake. Return to Chamba.', distance: '48 km', travelTime: '3–4 hours', sightseeing: ['Lakshmi Narayan Temple', 'Akhand Chandi Palace', 'Bhuri Singh Museum', 'Khajjiar Lake', 'Khajjiar Meadow'], overnight: 'Chamba' },
  { day: 3, title: 'Chamba to Pathankot – Departure', description: 'After breakfast, visit Chamunda Devi Temple. Depart for Pathankot. Trip ends.', distance: '120 km', travelTime: '4–5 hours', sightseeing: ['Chamunda Devi Temple'], overnight: 'Drop at Pathankot' }],

  vehicleDetails: 'Hatchback / Sedan / SUV / Innova. All AC vehicles with experienced hill drivers.',
  hotelDetails: 'Budget to premium hotels available in Chamba. Not included in package price.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'How far is Khajjiar from Chamba?', a: 'Khajjiar is approximately 24 km from Chamba, about 1 hour drive.' },
  { q: 'Is Chamba safe for families?', a: 'Yes, Chamba is very safe and family-friendly with good roads and facilities.' }]

},

// ─── DALHOUSIE & KHAJJIAR ────────────────────────────────────────────────────
{
  id: 'pkg-dalhousie-khajjiar-3d',
  name: 'Dalhousie & Khajjiar Weekend Package',
  destination: 'Pathankot → Dalhousie → Khajjiar, Himachal Pradesh',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_189f78386-1767172457483.png",
  alt: 'Dalhousie hill station with colonial buildings pine forests and snow-capped Dhauladhar mountains',
  duration: '3D / 2N',
  durationDays: 3,
  durationNights: 2,
  rating: 4.7,
  reviews: 243,
  price: 6999,
  originalPrice: 9000,
  discount: 22,
  category: 'Dalhousie & Khajjiar',
  travelType: ['Couple', 'Family', 'Honeymoon'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Khajjiar Lake', 'Dainkund Peak', 'Panchpula Waterfall', 'Satdhara Falls', 'Gandhi Chowk'],
  tag: 'Weekend Getaway',
  tagColor: 'badge-primary',
  season: ['Spring', 'Summer', 'Autumn', 'Winter'],
  bestTimeToVisit: 'October – June',
  budgetTier: 'budget',
  description: 'The perfect weekend escape from the plains! Dalhousie\'s colonial charm combined with Khajjiar\'s magical meadows make this one of North India\'s most loved short breaks.',
  pickupPricing: { pathankot: 6999, amritsar: 8999, chandigarh: 11999, delhiNCR: 16999 },
  itinerary: [
  { day: 1, title: 'Pathankot to Dalhousie', description: 'Depart from Pathankot. Drive to Dalhousie (80 km). Check in at hotel. Evening stroll at Gandhi Chowk and Subhash Chowk.', distance: '80 km', travelTime: '2.5–3 hours', sightseeing: ['Gandhi Chowk', 'Subhash Chowk'], overnight: 'Dalhousie' },
  { day: 2, title: 'Dalhousie Sightseeing & Khajjiar', description: 'Morning visit to Panchpula, Satdhara Falls, and Dainkund Peak. Drive to Khajjiar (24 km). Enjoy the golden meadow and lake. Return to Dalhousie.', distance: '50 km', travelTime: '4–5 hours', sightseeing: ['Panchpula', 'Satdhara Falls', 'Dainkund Peak', 'Khajjiar Lake', 'Khajjiar Meadow'], overnight: 'Dalhousie' },
  { day: 3, title: 'Dalhousie to Pathankot – Departure', description: 'After breakfast, visit St. Francis Church and Bakrota Hills. Depart for Pathankot.', distance: '80 km', travelTime: '2.5–3 hours', sightseeing: ['St. Francis Church', 'Bakrota Hills'], overnight: 'Drop at Pathankot' }],

  vehicleDetails: 'Hatchback / Sedan / SUV / Innova. All AC vehicles with experienced hill drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is Dalhousie good for honeymoon?', a: 'Yes! Dalhousie is one of the most romantic hill stations in North India, perfect for honeymoon couples.' },
  { q: 'What is the distance from Pathankot to Dalhousie?', a: 'Dalhousie is approximately 80 km from Pathankot, about 2.5–3 hours drive.' }]

},

// ─── DHARAMSHALA & MCLEODGANJ ─────────────────────────────────────────────────
{
  id: 'pkg-dharamshala-mcleodganj-4d',
  name: 'Dharamshala & McLeod Ganj Retreat',
  destination: 'Pathankot → Dharamshala → McLeod Ganj, Himachal Pradesh',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1acec545a-1786006696193.png',
  alt: 'McLeod Ganj Dharamshala with Tibetan prayer flags and Dhauladhar mountain range in background',
  duration: '4D / 3N',
  durationDays: 4,
  durationNights: 3,
  rating: 4.7,
  reviews: 298,
  price: 9999,
  originalPrice: 13000,
  discount: 23,
  category: 'Dharamshala & McLeodganj',
  travelType: ['Couple', 'Adventure', 'Group', 'Family'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Entry Tickets', 'Trek Guide Charges', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Triund Trek', 'Dalai Lama Temple', 'Bhagsu Waterfall', 'Namgyal Monastery', 'Tibetan Market'],
  tag: 'Spiritual Retreat',
  tagColor: 'badge-secondary',
  season: ['Spring', 'Summer', 'Autumn', 'Winter'],
  bestTimeToVisit: 'March – June, September – December',
  budgetTier: 'budget',
  description: 'Experience the spiritual energy of the Dalai Lama\'s residence, trek to the stunning Triund meadow, and immerse yourself in Tibetan culture in this little Lhasa of India.',
  pickupPricing: { pathankot: 9999, amritsar: 11999, chandigarh: 14999, delhiNCR: 19999 },
  itinerary: [
  { day: 1, title: 'Pathankot to Dharamshala', description: 'Depart from Pathankot. Drive to Dharamshala (90 km). Check in at hotel. Evening visit to Dharamshala Cricket Stadium and War Memorial.', distance: '90 km', travelTime: '3–4 hours', sightseeing: ['Dharamshala Cricket Stadium', 'War Memorial'], overnight: 'Dharamshala / McLeod Ganj' },
  { day: 2, title: 'McLeod Ganj Sightseeing', description: 'Visit Tsuglagkhang Complex (Dalai Lama Temple), Namgyal Monastery, Tibet Museum, and Bhagsu Waterfall. Explore the Tibetan market and cafes.', distance: '15 km local', travelTime: '6–7 hours', sightseeing: ['Dalai Lama Temple', 'Namgyal Monastery', 'Tibet Museum', 'Bhagsu Waterfall', 'Tibetan Market'], overnight: 'McLeod Ganj' },
  { day: 3, title: 'Triund Trek', description: 'Early morning trek to Triund (9 km one way). Enjoy panoramic views of Dhauladhar range. Return by evening. Explore local cafes.', distance: '18 km trek', travelTime: '6–7 hours', sightseeing: ['Triund Meadow', 'Dhauladhar Views', 'Snowline Café'], overnight: 'McLeod Ganj', notes: 'Carry trekking shoes, warm clothes, and water.' },
  { day: 4, title: 'McLeod Ganj to Pathankot – Departure', description: 'Morning visit to Kangra Fort (optional). Depart for Pathankot. Trip ends.', distance: '90 km', travelTime: '3–4 hours', overnight: 'Drop at Pathankot' }],

  vehicleDetails: 'Hatchback / Sedan / SUV / Innova. All AC vehicles with experienced hill drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is Triund trek safe for beginners?', a: 'Yes, Triund is a moderate trek suitable for beginners. A local guide is recommended.' },
  { q: 'Can we see the Dalai Lama?', a: 'The Dalai Lama\'s public audiences are announced on the official website. The temple complex is open to all visitors.' }]

},

// ─── BIR BILLING ─────────────────────────────────────────────────────────────
{
  id: 'pkg-bir-billing-3d',
  name: 'Bir Billing Paragliding Adventure',
  destination: 'Pathankot → Bir Billing, Kangra, Himachal Pradesh',
  image: "https://images.unsplash.com/photo-1713063968789-adf139c4a1eb",
  alt: 'Paraglider soaring over Bir Billing valley with green Himalayan hills and clouds below',
  duration: '3D / 2N',
  durationDays: 3,
  durationNights: 2,
  rating: 4.8,
  reviews: 176,
  price: 8499,
  originalPrice: 11000,
  discount: 23,
  category: 'Bir Billing',
  travelType: ['Adventure', 'Couple', 'Group'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Paragliding Charges', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Paragliding at Billing', 'Bir Tibetan Colony', 'Chokling Monastery', 'Tea Gardens', 'Sunset Point'],
  tag: 'Adventure',
  tagColor: 'badge-secondary',
  season: ['Spring', 'Summer', 'Autumn'],
  bestTimeToVisit: 'March – June, September – November',
  budgetTier: 'budget',
  description: 'Bir Billing is the paragliding capital of Asia and one of the best paragliding sites in the world. Experience the thrill of flying over the Kangra Valley with breathtaking Himalayan views.',
  pickupPricing: { pathankot: 8499, amritsar: 10499, chandigarh: 13499, delhiNCR: 18499 },
  itinerary: [
  { day: 1, title: 'Pathankot to Bir', description: 'Depart from Pathankot. Drive to Bir (130 km). Check in at camp/hotel. Evening explore Tibetan Colony and Chokling Monastery.', distance: '130 km', travelTime: '4–5 hours', sightseeing: ['Tibetan Colony', 'Chokling Monastery'], overnight: 'Bir' },
  { day: 2, title: 'Paragliding at Billing', description: 'Drive to Billing (14 km). Experience tandem paragliding over the Kangra Valley. Return to Bir. Afternoon visit to tea gardens and Deer Park Institute.', distance: '28 km', travelTime: '4–5 hours', sightseeing: ['Billing Paragliding Site', 'Tea Gardens', 'Deer Park Institute'], overnight: 'Bir', notes: 'Paragliding charges are extra (approx ₹2,500–₹3,500 per person).' },
  { day: 3, title: 'Bir to Pathankot – Departure', description: 'Morning visit to Sherabling Monastery. Depart for Pathankot. Trip ends.', distance: '130 km', travelTime: '4–5 hours', sightseeing: ['Sherabling Monastery'], overnight: 'Drop at Pathankot' }],

  vehicleDetails: 'Hatchback / Sedan / SUV / Innova. All AC vehicles with experienced hill drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is paragliding safe at Bir Billing?', a: 'Yes, Bir Billing is a world-class paragliding site with certified pilots. Tandem flights are very safe.' },
  { q: 'What is the cost of paragliding?', a: 'Paragliding charges are approximately ₹2,500–₹3,500 per person and are not included in the package.' }]

},

// ─── SPITI VALLEY ─────────────────────────────────────────────────────────────
{
  id: 'pkg-spiti-circuit-9d',
  name: 'Spiti Valley Adventure Circuit',
  destination: 'Shimla → Kaza → Tabo → Chandratal → Manali',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12a58ef13-1780915438416.png",
  alt: 'Spiti Valley high altitude desert landscape with ancient Key Monastery on cliff and barren mountains',
  duration: '9D / 8N',
  durationDays: 9,
  durationNights: 8,
  rating: 4.8,
  reviews: 156,
  price: 27500,
  originalPrice: 35000,
  discount: 21,
  category: 'Spiti Valley',
  travelType: ['Adventure', 'Group'],
  includes: ['Vehicle (SUV/Innova)', 'Driver', 'Fuel', 'Toll Tax', 'State Tax', 'Inner Line Permits'],
  excludes: ['Hotel/Camping', 'Meals', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance', 'Porter/Pony Charges'],
  highlights: ['Key Monastery', 'Chandratal Lake', 'Kunzum Pass', 'Dhankar Monastery', 'Pin Valley', 'Kibber Village'],
  tag: 'Adventure',
  tagColor: 'badge-secondary',
  season: ['Summer'],
  bestTimeToVisit: 'June – September',
  budgetTier: 'mid',
  description: 'The road less travelled — high altitude desert, ancient monasteries, and starlit camping. Spiti Valley is a cold desert mountain valley in the Himalayas, one of the most remote regions of India.',
  pickupPricing: { pathankot: 27500, amritsar: 30500, chandigarh: 27500, delhiNCR: 32500 },
  itinerary: [
  { day: 1, title: 'Chandigarh / Shimla to Narkanda', description: 'Depart from Chandigarh/Shimla. Drive to Narkanda (65 km from Shimla). Overnight stay.', distance: '65 km from Shimla', travelTime: '3–4 hours', overnight: 'Narkanda' },
  { day: 2, title: 'Narkanda to Rekong Peo (Kalpa)', description: 'Drive through Rampur to Rekong Peo. Visit Kalpa village with stunning Kinnaur Kailash views.', distance: '200 km', travelTime: '7–8 hours', sightseeing: ['Kalpa Village', 'Kinnaur Kailash View'], overnight: 'Rekong Peo / Kalpa' },
  { day: 3, title: 'Rekong Peo to Tabo', description: 'Drive through Pooh and Nako to Tabo. Visit the 1000-year-old Tabo Monastery.', distance: '170 km', travelTime: '6–7 hours', sightseeing: ['Nako Lake', 'Tabo Monastery'], overnight: 'Tabo' },
  { day: 4, title: 'Tabo to Kaza', description: 'Drive to Kaza via Dhankar. Visit Dhankar Monastery and Dhankar Lake.', distance: '70 km', travelTime: '3–4 hours', sightseeing: ['Dhankar Monastery', 'Dhankar Lake'], overnight: 'Kaza' },
  { day: 5, title: 'Kaza Local Sightseeing', description: 'Visit Key Monastery, Kibber Village, Chicham Bridge, and Pin Valley.', distance: '80 km local', travelTime: '6–7 hours', sightseeing: ['Key Monastery', 'Kibber Village', 'Chicham Bridge', 'Pin Valley'], overnight: 'Kaza' },
  { day: 6, title: 'Kaza to Langza – Komic – Hikkim', description: 'Visit the highest post office (Hikkim), Komic village, and Langza Buddha statue.', distance: '60 km', travelTime: '4–5 hours', sightseeing: ['Hikkim Post Office', 'Komic Village', 'Langza Buddha'], overnight: 'Kaza' },
  { day: 7, title: 'Kaza to Chandratal Lake', description: 'Drive to Chandratal Lake via Kunzum Pass. Camp near the stunning crescent-shaped lake.', distance: '100 km', travelTime: '5–6 hours', sightseeing: ['Kunzum Pass', 'Chandratal Lake'], overnight: 'Chandratal Camp', notes: 'Carry warm clothes. Temperature drops below 0°C at night.' },
  { day: 8, title: 'Chandratal to Manali', description: 'Drive from Chandratal to Manali via Rohtang Pass. Check in at hotel.', distance: '115 km', travelTime: '5–6 hours', sightseeing: ['Rohtang Pass', 'Solang Valley'], overnight: 'Manali' },
  { day: 9, title: 'Manali to Chandigarh – Departure', description: 'Depart from Manali. Drive to Chandigarh. Trip ends.', distance: '310 km', travelTime: '9–10 hours', overnight: 'Drop at Chandigarh' }],

  vehicleDetails: 'Innova Crysta or equivalent SUV mandatory for Spiti. Experienced high-altitude drivers.',
  hotelDetails: 'Guesthouses and camps available at each destination. Not included in package price.',
  bookingPolicy: 'Minimum 30% advance required for Spiti packages. Balance 15 days before departure.',
  cancellationPolicy: '15+ days before: 80% refund. 7–15 days: 50% refund. Less than 7 days: No refund.',
  faqs: [
  { q: 'Is Spiti accessible by car?', a: 'Yes, Spiti is accessible by road from Shimla (via Kinnaur) or Manali (via Rohtang). The Manali route is open June–October only.' },
  { q: 'Do I need permits for Spiti?', a: 'Inner Line Permits are required for certain areas near the Indo-China border. We arrange all permits.' }]

},

// ─── KINNAUR ─────────────────────────────────────────────────────────────────
{
  id: 'pkg-kinnaur-5d',
  name: 'Kinnaur Valley Apple Trail Tour',
  destination: 'Shimla → Sarahan → Sangla → Chitkul → Kalpa',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1189dd812-1774895671722.png",
  alt: 'Kinnaur Valley with apple orchards terraced fields and snow-capped Kinnaur Kailash peak Himachal Pradesh',
  duration: '5D / 4N',
  durationDays: 5,
  durationNights: 4,
  rating: 4.7,
  reviews: 134,
  price: 14999,
  originalPrice: 19000,
  discount: 21,
  category: 'Kinnaur',
  travelType: ['Adventure', 'Couple', 'Family'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Chitkul (Last Village)', 'Sangla Valley', 'Kinnaur Kailash', 'Sarahan Bhimakali Temple', 'Kalpa Village'],
  tag: 'Hidden Gem',
  tagColor: 'badge-accent',
  season: ['Summer', 'Autumn'],
  bestTimeToVisit: 'May – October',
  budgetTier: 'mid',
  description: 'Kinnaur is a land of apple orchards, ancient temples, and dramatic mountain scenery. Visit Chitkul — the last inhabited village near the Indo-China border — and experience the unique Kinnauri culture.',
  pickupPricing: { pathankot: 17999, amritsar: 20999, chandigarh: 14999, delhiNCR: 19999 },
  itinerary: [
  { day: 1, title: 'Shimla to Sarahan', description: 'Depart from Shimla. Drive to Sarahan (170 km). Visit Bhimakali Temple. Overnight stay.', distance: '170 km', travelTime: '6–7 hours', sightseeing: ['Bhimakali Temple'], overnight: 'Sarahan' },
  { day: 2, title: 'Sarahan to Sangla Valley', description: 'Drive to Sangla Valley (90 km). Visit Kamru Fort and Sangla village. Explore the apple orchards.', distance: '90 km', travelTime: '4–5 hours', sightseeing: ['Kamru Fort', 'Sangla Village', 'Apple Orchards'], overnight: 'Sangla' },
  { day: 3, title: 'Sangla to Chitkul', description: 'Drive to Chitkul (28 km) — the last inhabited village on the Indo-China border. Explore the village and Baspa River. Return to Sangla.', distance: '56 km', travelTime: '3–4 hours', sightseeing: ['Chitkul Village', 'Baspa River', 'Old Chitkul Temple'], overnight: 'Sangla' },
  { day: 4, title: 'Sangla to Kalpa', description: 'Drive to Kalpa (60 km). Enjoy stunning views of Kinnaur Kailash peak. Visit Hu-Bu-Lan-Kar Monastery.', distance: '60 km', travelTime: '3–4 hours', sightseeing: ['Kinnaur Kailash View', 'Hu-Bu-Lan-Kar Monastery', 'Suicide Point'], overnight: 'Kalpa' },
  { day: 5, title: 'Kalpa to Shimla – Departure', description: 'Drive back to Shimla via Rampur. Trip ends.', distance: '200 km', travelTime: '7–8 hours', overnight: 'Drop at Shimla' }],

  vehicleDetails: 'SUV (Innova/Ertiga) recommended for Kinnaur roads. Experienced hill drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is Chitkul accessible by car?', a: 'Yes, Chitkul is accessible by road. The road is narrow but motorable for SUVs.' },
  { q: 'What is the best time to visit Kinnaur?', a: 'May to October is the best time. September–October is apple harvest season.' }]

},

// ─── SHIMLA ──────────────────────────────────────────────────────────────────
{
  id: 'pkg-shimla-3d',
  name: 'Shimla Queen of Hills Tour',
  destination: 'Chandigarh → Shimla → Kufri, Himachal Pradesh',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1df48b71a-1772110201244.png",
  alt: 'Shimla Mall Road with colonial buildings and snow-covered mountains in background Himachal Pradesh',
  duration: '3D / 2N',
  durationDays: 3,
  durationNights: 2,
  rating: 4.6,
  reviews: 389,
  price: 8999,
  originalPrice: 12000,
  discount: 25,
  category: 'Shimla',
  travelType: ['Family', 'Couple', 'Honeymoon', 'Group'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Mall Road', 'The Ridge', 'Kufri', 'Jakhu Temple', 'Christ Church', 'Toy Train'],
  tag: 'Best Seller',
  tagColor: 'badge-primary',
  season: ['Winter', 'Spring', 'Summer'],
  bestTimeToVisit: 'October – June',
  budgetTier: 'budget',
  description: 'The Queen of Hills — Shimla is India\'s most popular hill station with its colonial architecture, scenic Mall Road, and snow-covered Kufri. A perfect family and honeymoon destination.',
  pickupPricing: { pathankot: 11999, amritsar: 13999, chandigarh: 8999, delhiNCR: 14999 },
  itinerary: [
  { day: 1, title: 'Chandigarh to Shimla', description: 'Depart from Chandigarh. Drive to Shimla (115 km). Check in at hotel. Evening stroll on Mall Road and The Ridge.', distance: '115 km', travelTime: '4–5 hours', sightseeing: ['Mall Road', 'The Ridge', 'Christ Church'], overnight: 'Shimla' },
  { day: 2, title: 'Shimla Sightseeing & Kufri', description: 'Morning visit to Jakhu Temple (Hanuman statue). Drive to Kufri (16 km) for snow activities. Visit Himalayan Nature Park. Return to Shimla.', distance: '32 km', travelTime: '5–6 hours', sightseeing: ['Jakhu Temple', 'Kufri Snow Point', 'Himalayan Nature Park', 'Chail'], overnight: 'Shimla' },
  { day: 3, title: 'Shimla to Chandigarh – Departure', description: 'Morning visit to Viceregal Lodge (Rashtrapati Niwas). Depart for Chandigarh. Trip ends.', distance: '115 km', travelTime: '4–5 hours', sightseeing: ['Viceregal Lodge', 'Scandal Point'], overnight: 'Drop at Chandigarh' }],

  vehicleDetails: 'Hatchback / Sedan / SUV / Innova. All AC vehicles with experienced hill drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is Shimla good in winter?', a: 'Yes! Shimla receives snowfall from December to February, making it magical in winter.' },
  { q: 'How far is Kufri from Shimla?', a: 'Kufri is 16 km from Shimla, approximately 45 minutes drive.' }]

},

// ─── MANALI ──────────────────────────────────────────────────────────────────
{
  id: 'pkg-manali-5d',
  name: 'Manali Adventure & Snow Tour',
  destination: 'Pathankot → Manali → Rohtang Pass, Himachal Pradesh',
  image: "https://images.unsplash.com/photo-1702360373561-9b7c7fc813d8",
  alt: 'Snow-covered Rohtang Pass mountain road with pine trees and blue sky Manali Himachal Pradesh',
  duration: '5D / 4N',
  durationDays: 5,
  durationNights: 4,
  rating: 4.8,
  reviews: 456,
  price: 12999,
  originalPrice: 17000,
  discount: 24,
  category: 'Manali',
  travelType: ['Family', 'Couple', 'Honeymoon', 'Adventure', 'Group'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Rohtang Permit', 'Snow Activities', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Rohtang Pass', 'Solang Valley', 'Hadimba Temple', 'Old Manali', 'Beas River', 'Vashisht Hot Springs'],
  tag: 'Most Popular',
  tagColor: 'badge-primary',
  season: ['Summer', 'Winter'],
  bestTimeToVisit: 'October – June',
  budgetTier: 'mid',
  description: 'Manali — the adventure capital of North India. From snow-covered Rohtang Pass to the serene Solang Valley, from ancient Hadimba Temple to the vibrant Old Manali, this tour has it all.',
  pickupPricing: { pathankot: 12999, amritsar: 14999, chandigarh: 12999, delhiNCR: 17999 },
  itinerary: [
  { day: 1, title: 'Pathankot to Manali', description: 'Depart from Pathankot early morning. Drive to Manali (280 km) via Mandi. Check in at hotel. Evening explore Mall Road.', distance: '280 km', travelTime: '9–10 hours', sightseeing: ['Manali Mall Road', 'Manu Temple'], overnight: 'Manali' },
  { day: 2, title: 'Rohtang Pass / Solang Valley', description: 'Early morning drive to Rohtang Pass (51 km). Enjoy snow activities. Return via Solang Valley for adventure sports.', distance: '100 km', travelTime: '7–8 hours', sightseeing: ['Rohtang Pass', 'Solang Valley', 'Beas Kund'], overnight: 'Manali', notes: 'Rohtang Pass permit required (₹500–₹600 extra). Carry warm clothes.' },
  { day: 3, title: 'Manali Local Sightseeing', description: 'Visit Hadimba Temple, Vashisht Hot Springs, Club House, and Old Manali. Evening at leisure.', distance: '30 km local', travelTime: '5–6 hours', sightseeing: ['Hadimba Temple', 'Vashisht Hot Springs', 'Club House', 'Old Manali', 'Tibetan Monastery'], overnight: 'Manali' },
  { day: 4, title: 'Manali to Kullu – Naggar', description: 'Drive to Naggar Castle (22 km). Visit Roerich Art Gallery. Drive to Kullu for river rafting. Return to Manali.', distance: '80 km', travelTime: '5–6 hours', sightseeing: ['Naggar Castle', 'Roerich Art Gallery', 'Kullu Rafting'], overnight: 'Manali' },
  { day: 5, title: 'Manali to Pathankot – Departure', description: 'Depart from Manali. Drive back to Pathankot. Trip ends.', distance: '280 km', travelTime: '9–10 hours', overnight: 'Drop at Pathankot' }],

  vehicleDetails: 'Hatchback / Sedan / SUV / Innova. All AC vehicles with experienced hill drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is Rohtang Pass open in winter?', a: 'Rohtang Pass is closed from November to May due to heavy snowfall. Solang Valley is accessible year-round.' },
  { q: 'Do I need a permit for Rohtang Pass?', a: 'Yes, a permit is required for Rohtang Pass (₹500–₹600 per vehicle). We can arrange it.' }]

},

// ─── SHIMLA + MANALI COMBO ────────────────────────────────────────────────────
{
  id: 'pkg-shimla-manali-7d',
  name: 'Shimla & Manali Winter Escape',
  destination: 'Chandigarh → Shimla → Manali, Himachal Pradesh',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_181dc8ef9-1772966814140.png",
  alt: 'Snow-covered Rohtang Pass mountain road with pine trees and blue sky Himachal Pradesh',
  duration: '7D / 6N',
  durationDays: 7,
  durationNights: 6,
  rating: 4.8,
  reviews: 534,
  price: 18500,
  originalPrice: 24000,
  discount: 23,
  category: 'Manali',
  travelType: ['Family', 'Couple', 'Honeymoon', 'Group'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Rohtang Permit', 'Snow Activities', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Shimla Mall Road', 'Kufri Snow Point', 'Rohtang Pass', 'Solang Valley', 'Hadimba Temple', 'Vashisht Hot Springs'],
  tag: 'Best Seller',
  tagColor: 'badge-primary',
  season: ['Winter', 'Spring', 'Summer'],
  bestTimeToVisit: 'October – June',
  budgetTier: 'mid',
  description: 'The ultimate Himachal Pradesh experience — combine the colonial charm of Shimla with the adventure of Manali in one perfectly crafted 7-day tour.',
  pickupPricing: { pathankot: 20500, amritsar: 22500, chandigarh: 18500, delhiNCR: 24500 },
  itinerary: [
  { day: 1, title: 'Chandigarh to Shimla', description: 'Depart from Chandigarh. Drive to Shimla (115 km). Check in. Evening Mall Road walk.', distance: '115 km', travelTime: '4–5 hours', overnight: 'Shimla' },
  { day: 2, title: 'Shimla Sightseeing & Kufri', description: 'Jakhu Temple, Kufri, Himalayan Nature Park, Chail.', distance: '50 km', travelTime: '6 hours', overnight: 'Shimla' },
  { day: 3, title: 'Shimla to Manali', description: 'Drive from Shimla to Manali via Mandi (260 km). Check in at hotel.', distance: '260 km', travelTime: '8–9 hours', overnight: 'Manali' },
  { day: 4, title: 'Rohtang Pass / Solang Valley', description: 'Rohtang Pass snow activities, Solang Valley adventure sports.', distance: '100 km', travelTime: '7–8 hours', overnight: 'Manali' },
  { day: 5, title: 'Manali Local Sightseeing', description: 'Hadimba Temple, Vashisht Hot Springs, Old Manali, Tibetan Monastery.', distance: '30 km', travelTime: '5–6 hours', overnight: 'Manali' },
  { day: 6, title: 'Kullu & Naggar', description: 'Naggar Castle, Roerich Art Gallery, Kullu river rafting.', distance: '80 km', travelTime: '5–6 hours', overnight: 'Manali' },
  { day: 7, title: 'Manali to Chandigarh – Departure', description: 'Drive back to Chandigarh. Trip ends.', distance: '310 km', travelTime: '9–10 hours', overnight: 'Drop at Chandigarh' }],

  vehicleDetails: 'SUV (Innova/Ertiga) recommended. All AC vehicles with experienced hill drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Can we do Shimla and Manali in 7 days?', a: 'Yes, 7 days is ideal for covering both Shimla and Manali comfortably.' }]

},

// ─── KASHMIR ─────────────────────────────────────────────────────────────────
{
  id: 'pkg-kashmir-paradise-6d',
  name: 'Kashmir Paradise Tour',
  destination: 'Srinagar → Gulmarg → Pahalgam → Sonamarg',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1523aff28-1772248815701.png',
  alt: 'Dal Lake Kashmir with shikaras and snow-capped Himalayan mountains in background at sunrise',
  duration: '6D / 5N',
  durationDays: 6,
  durationNights: 5,
  rating: 4.9,
  reviews: 412,
  price: 22000,
  originalPrice: 28000,
  discount: 21,
  category: 'Kashmir',
  travelType: ['Couple', 'Honeymoon', 'Family', 'Group'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax', 'Shikara Ride (1 hour)'],
  excludes: ['Hotel/Houseboat', 'Meals', 'Parking Charges', 'Gondola Charges', 'Pony Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Dal Lake Shikara', 'Gulmarg Gondola', 'Betaab Valley', 'Sonamarg Glacier', 'Mughal Gardens', 'Pahalgam Valley'],
  tag: 'Top Rated',
  tagColor: 'badge-accent',
  season: ['Summer', 'Autumn'],
  bestTimeToVisit: 'April – October',
  budgetTier: 'mid',
  description: 'The crown jewel of Indian tourism — Dal Lake houseboats, Gulmarg skiing, Pahalgam valleys, and Sonamarg glaciers. Kashmir is heaven on earth and a must-visit destination.',
  pickupPricing: { pathankot: 22000, amritsar: 22000, chandigarh: 24000, delhiNCR: 26000 },
  itinerary: [
  { day: 1, title: 'Arrival Srinagar – Dal Lake', description: 'Arrive at Srinagar Airport. Transfer to houseboat on Dal Lake. Evening Shikara ride on Dal Lake. Visit floating vegetable market.', distance: 'Local', travelTime: '1–2 hours', sightseeing: ['Dal Lake', 'Shikara Ride', 'Floating Market'], overnight: 'Houseboat / Hotel Srinagar' },
  { day: 2, title: 'Srinagar Mughal Gardens', description: 'Visit Nishat Bagh, Shalimar Bagh, Chashme Shahi, and Pari Mahal. Evening at Lal Chowk.', distance: '40 km', travelTime: '5–6 hours', sightseeing: ['Nishat Bagh', 'Shalimar Bagh', 'Chashme Shahi', 'Pari Mahal', 'Hazratbal Mosque'], overnight: 'Srinagar' },
  { day: 3, title: 'Gulmarg Day Trip', description: 'Drive to Gulmarg (56 km). Take the Gondola to Phase 1 (Kongdori) and Phase 2 (Apharwat Peak). Enjoy snow activities.', distance: '112 km', travelTime: '5–6 hours', sightseeing: ['Gulmarg Gondola', 'Apharwat Peak', 'Strawberry Valley'], overnight: 'Srinagar', notes: 'Gondola charges extra (₹700–₹900 per person per phase).' },
  { day: 4, title: 'Pahalgam Valley', description: 'Drive to Pahalgam (96 km). Visit Betaab Valley, Aru Valley, and Chandanwari. Enjoy pony rides.', distance: '192 km', travelTime: '6–7 hours', sightseeing: ['Betaab Valley', 'Aru Valley', 'Chandanwari', 'Lidder River'], overnight: 'Pahalgam / Srinagar' },
  { day: 5, title: 'Sonamarg Glacier', description: 'Drive to Sonamarg (87 km). Visit Thajiwas Glacier by pony/vehicle. Return to Srinagar.', distance: '174 km', travelTime: '6–7 hours', sightseeing: ['Sonamarg', 'Thajiwas Glacier', 'Sindh River'], overnight: 'Srinagar' },
  { day: 6, title: 'Srinagar Departure', description: 'Morning visit to Shankaracharya Temple. Transfer to Srinagar Airport. Trip ends.', distance: '15 km', travelTime: '1 hour', sightseeing: ['Shankaracharya Temple'], overnight: 'Drop at Airport' }],

  vehicleDetails: 'Innova / SUV recommended for Kashmir. All AC vehicles with experienced drivers.',
  hotelDetails: 'Houseboats on Dal Lake or hotels in Srinagar. Not included in package price.',
  bookingPolicy: 'Minimum 25% advance required for Kashmir packages.',
  cancellationPolicy: '10+ days before: 80% refund. 5–10 days: 50% refund. Less than 5 days: No refund.',
  faqs: [
  { q: 'Is Kashmir safe to visit?', a: 'Yes, Kashmir is safe for tourists. Millions of tourists visit every year without any issues.' },
  { q: 'What is the best time to visit Kashmir?', a: 'April–June for flowers and greenery. December–February for snow. July–September for Amarnath Yatra.' }]

},

// ─── LEH LADAKH ──────────────────────────────────────────────────────────────
{
  id: 'pkg-leh-ladakh-10d',
  name: 'Leh Ladakh Panorama Tour',
  destination: 'Manali → Leh → Nubra Valley → Pangong Lake',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b9a1afe0-1783157229295.png',
  alt: 'Pangong Tso lake with turquoise water and Himalayan mountains reflecting in clear blue water Ladakh',
  duration: '10D / 9N',
  durationDays: 10,
  durationNights: 9,
  rating: 4.9,
  reviews: 267,
  price: 32000,
  originalPrice: 40000,
  discount: 20,
  category: 'Leh Ladakh',
  travelType: ['Adventure', 'Couple', 'Group'],
  includes: ['Vehicle (Innova)', 'Driver', 'Fuel', 'Toll Tax', 'State Tax', 'Inner Line Permits'],
  excludes: ['Hotel/Camping', 'Meals', 'Parking Charges', 'Entry Tickets', 'Oxygen Cylinder', 'Personal Expenses', 'Travel Insurance', 'Flight Tickets'],
  highlights: ['Pangong Lake', 'Nubra Valley', 'Khardung La Pass', 'Diskit Monastery', 'Magnetic Hill', 'Leh Palace', 'Hemis Monastery'],
  tag: 'Premium',
  tagColor: 'badge-primary',
  season: ['Summer'],
  bestTimeToVisit: 'June – September',
  budgetTier: 'premium',
  description: 'The ultimate Himalayan odyssey — the world\'s highest motorable roads, turquoise Pangong Lake, sand dunes of Nubra Valley, and ancient monasteries. Leh Ladakh is a bucket-list destination.',
  pickupPricing: { pathankot: 35000, amritsar: 35000, chandigarh: 32000, delhiNCR: 36000 },
  itinerary: [
  { day: 1, title: 'Manali to Jispa', description: 'Depart from Manali early morning. Drive to Jispa via Rohtang Pass and Keylong. Acclimatization day.', distance: '145 km', travelTime: '6–7 hours', overnight: 'Jispa' },
  { day: 2, title: 'Jispa to Leh', description: 'Drive from Jispa to Leh via Baralacha La, Lachulung La, and Tanglang La. Arrive at Leh. Rest for acclimatization.', distance: '365 km', travelTime: '12–13 hours', overnight: 'Leh', notes: 'Avoid exertion on Day 1 and 2 in Leh for acclimatization.' },
  { day: 3, title: 'Leh Acclimatization & Local Sightseeing', description: 'Visit Leh Palace, Shanti Stupa, and Leh Market. Short walks only.', distance: '20 km', travelTime: '4–5 hours', sightseeing: ['Leh Palace', 'Shanti Stupa', 'Leh Market'], overnight: 'Leh' },
  { day: 4, title: 'Leh to Nubra Valley via Khardung La', description: 'Drive to Nubra Valley via Khardung La (5,359m) — world\'s highest motorable road. Visit Diskit Monastery and sand dunes.', distance: '125 km', travelTime: '5–6 hours', sightseeing: ['Khardung La Pass', 'Diskit Monastery', 'Hunder Sand Dunes', 'Bactrian Camel Ride'], overnight: 'Nubra Valley' },
  { day: 5, title: 'Nubra Valley to Pangong Lake', description: 'Drive from Nubra to Pangong Lake via Shyok Valley (160 km). Arrive at Pangong. Sunset at the lake.', distance: '160 km', travelTime: '6–7 hours', sightseeing: ['Pangong Tso Lake', 'Merak Village'], overnight: 'Pangong Lake Camp' },
  { day: 6, title: 'Pangong Lake to Leh', description: 'Sunrise at Pangong Lake. Drive back to Leh via Chang La Pass (5,360m).', distance: '160 km', travelTime: '5–6 hours', sightseeing: ['Pangong Sunrise', 'Chang La Pass', 'Thiksey Monastery'], overnight: 'Leh' },
  { day: 7, title: 'Leh to Tso Moriri (Optional)', description: 'Drive to Tso Moriri Lake (220 km). Visit Korzok Monastery. Return to Leh next day.', distance: '220 km', travelTime: '7–8 hours', sightseeing: ['Tso Moriri Lake', 'Korzok Monastery'], overnight: 'Tso Moriri / Leh' },
  { day: 8, title: 'Leh Local – Monasteries Tour', description: 'Visit Hemis Monastery, Thiksey Monastery, Shey Palace, and Rancho School (3 Idiots location).', distance: '60 km', travelTime: '6–7 hours', sightseeing: ['Hemis Monastery', 'Thiksey Monastery', 'Shey Palace', 'Rancho School'], overnight: 'Leh' },
  { day: 9, title: 'Leh to Srinagar (via Kargil)', description: 'Drive from Leh to Srinagar via Kargil and Zoji La Pass. Overnight at Kargil or Srinagar.', distance: '430 km', travelTime: '12–13 hours', sightseeing: ['Kargil War Memorial', 'Drass Valley', 'Zoji La Pass'], overnight: 'Kargil / Srinagar' },
  { day: 10, title: 'Srinagar Departure', description: 'Transfer to Srinagar Airport. Trip ends.', distance: '15 km', travelTime: '1 hour', overnight: 'Drop at Airport' }],

  vehicleDetails: 'Innova Crysta mandatory for Ladakh. Experienced high-altitude drivers with oxygen cylinders.',
  hotelDetails: 'Guesthouses and camps at each destination. Not included in package price.',
  bookingPolicy: 'Minimum 30% advance required for Ladakh packages. Balance 15 days before departure.',
  cancellationPolicy: '15+ days before: 80% refund. 7–15 days: 50% refund. Less than 7 days: No refund.',
  faqs: [
  { q: 'Do I need permits for Ladakh?', a: 'Inner Line Permits are required for Nubra Valley, Pangong Lake, and Tso Moriri. We arrange all permits.' },
  { q: 'Is altitude sickness a concern?', a: 'Yes. We recommend spending 2 days in Leh for acclimatization before proceeding to higher altitudes.' }]

},

// ─── UTTARAKHAND ─────────────────────────────────────────────────────────────
{
  id: 'pkg-char-dham-12d',
  name: 'Char Dham Yatra Package',
  destination: 'Haridwar → Yamunotri → Gangotri → Kedarnath → Badrinath',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1244e3ddf-1782790554242.png',
  alt: 'Badrinath temple in Uttarakhand surrounded by snow-capped Himalayan peaks and green valley',
  duration: '12D / 11N',
  durationDays: 12,
  durationNights: 11,
  rating: 4.8,
  reviews: 298,
  price: 24500,
  originalPrice: 30000,
  discount: 18,
  category: 'Uttarakhand',
  travelType: ['Pilgrimage', 'Family', 'Group'],
  includes: ['Vehicle (Tempo Traveller/Innova)', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel/Dharamshala', 'Meals', 'Parking Charges', 'Pony/Palki Charges', 'Entry Tickets', 'Helicopter Charges', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Yamunotri Temple', 'Gangotri Glacier', 'Kedarnath Temple', 'Badrinath Temple', 'Rishikesh', 'Haridwar Ganga Aarti'],
  tag: 'Pilgrimage',
  tagColor: 'badge-secondary',
  season: ['Summer', 'Spring'],
  bestTimeToVisit: 'May – June, September – October',
  budgetTier: 'mid',
  description: 'A sacred journey to the four holy shrines of Uttarakhand — Yamunotri, Gangotri, Kedarnath, and Badrinath. The Char Dham Yatra is a lifetime pilgrimage experience for every Hindu devotee.',
  pickupPricing: { pathankot: 27500, amritsar: 27500, chandigarh: 24500, delhiNCR: 24500 },
  itinerary: [
  { day: 1, title: 'Delhi / Haridwar – Arrival', description: 'Arrive at Haridwar. Check in at hotel. Evening Ganga Aarti at Har Ki Pauri.', distance: 'Local', travelTime: '1 hour', sightseeing: ['Har Ki Pauri', 'Ganga Aarti'], overnight: 'Haridwar' },
  { day: 2, title: 'Haridwar to Barkot (Yamunotri Base)', description: 'Drive to Barkot (200 km). Check in at hotel.', distance: '200 km', travelTime: '7–8 hours', overnight: 'Barkot' },
  { day: 3, title: 'Yamunotri Darshan', description: 'Drive to Janki Chatti (14 km). Trek 6 km to Yamunotri Temple. Take holy dip. Return to Barkot.', distance: '14 km drive + 12 km trek', travelTime: '7–8 hours', sightseeing: ['Yamunotri Temple', 'Surya Kund'], overnight: 'Barkot' },
  { day: 4, title: 'Barkot to Uttarkashi (Gangotri Base)', description: 'Drive to Uttarkashi (100 km). Visit Vishwanath Temple.', distance: '100 km', travelTime: '4–5 hours', overnight: 'Uttarkashi' },
  { day: 5, title: 'Gangotri Darshan', description: 'Drive to Gangotri (100 km). Visit Gangotri Temple and Gaumukh Glacier viewpoint. Return to Uttarkashi.', distance: '200 km', travelTime: '7–8 hours', sightseeing: ['Gangotri Temple', 'Bhagirathi River', 'Gaumukh Viewpoint'], overnight: 'Uttarkashi' },
  { day: 6, title: 'Uttarkashi to Guptkashi (Kedarnath Base)', description: 'Drive to Guptkashi (220 km) via Rishikesh.', distance: '220 km', travelTime: '8–9 hours', overnight: 'Guptkashi' },
  { day: 7, title: 'Kedarnath Darshan', description: 'Drive to Sonprayag/Gaurikund. Trek 16 km to Kedarnath Temple (or helicopter). Darshan and return.', distance: '16 km trek', travelTime: '8–10 hours', sightseeing: ['Kedarnath Temple', 'Bhairavnath Temple'], overnight: 'Guptkashi', notes: 'Helicopter service available (extra charges). Pony/Palki available.' },
  { day: 8, title: 'Guptkashi to Badrinath', description: 'Drive to Badrinath (200 km) via Joshimath.', distance: '200 km', travelTime: '7–8 hours', overnight: 'Badrinath' },
  { day: 9, title: 'Badrinath Darshan', description: 'Early morning Abhishek Pooja. Visit Badrinath Temple, Tapt Kund, Brahma Kapal, and Mana Village.', distance: '10 km local', travelTime: '5–6 hours', sightseeing: ['Badrinath Temple', 'Tapt Kund', 'Brahma Kapal', 'Mana Village', 'Vasudhara Falls'], overnight: 'Badrinath' },
  { day: 10, title: 'Badrinath to Rudraprayag', description: 'Drive to Rudraprayag (160 km). Visit Rudranath Temple.', distance: '160 km', travelTime: '5–6 hours', overnight: 'Rudraprayag' },
  { day: 11, title: 'Rudraprayag to Rishikesh', description: 'Drive to Rishikesh (140 km). Visit Ram Jhula, Laxman Jhula, and Triveni Ghat.', distance: '140 km', travelTime: '5–6 hours', sightseeing: ['Ram Jhula', 'Laxman Jhula', 'Triveni Ghat'], overnight: 'Rishikesh' },
  { day: 12, title: 'Rishikesh to Haridwar – Departure', description: 'Morning Ganga Aarti at Rishikesh. Drive to Haridwar. Trip ends.', distance: '25 km', travelTime: '1 hour', overnight: 'Drop at Haridwar' }],

  vehicleDetails: 'Tempo Traveller (12 seater) for groups / Innova for families. Experienced hill drivers.',
  bookingPolicy: 'Minimum 25% advance required for Char Dham packages.',
  cancellationPolicy: '10+ days before: 80% refund. 5–10 days: 50% refund. Less than 5 days: No refund.',
  faqs: [
  { q: 'When does Char Dham Yatra open?', a: 'Char Dham Yatra typically opens in April–May and closes in October–November.' },
  { q: 'Is helicopter available for Kedarnath?', a: 'Yes, helicopter service is available for Kedarnath. Charges are extra (approx ₹3,000–₹5,000 per person).' }]

},

// ─── RAJASTHAN ────────────────────────────────────────────────────────────────
{
  id: 'pkg-rajasthan-heritage-8d',
  name: 'Royal Rajasthan Heritage Tour',
  destination: 'Jaipur → Jodhpur → Jaisalmer → Udaipur',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_15385a47b-1773056596554.png',
  alt: 'Amber Fort Jaipur illuminated at sunset with golden light on sandstone walls and lake below',
  duration: '8D / 7N',
  durationDays: 8,
  durationNights: 7,
  rating: 4.7,
  reviews: 289,
  price: 21000,
  originalPrice: 26500,
  discount: 21,
  category: 'Rajasthan',
  travelType: ['Family', 'Couple', 'Honeymoon', 'Group'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Entry Tickets', 'Camel Safari', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Amber Fort', 'Mehrangarh Fort', 'Jaisalmer Fort', 'City Palace Udaipur', 'Camel Safari', 'Desert Camp'],
  tag: 'Heritage',
  tagColor: 'badge-accent',
  season: ['Winter', 'Autumn'],
  bestTimeToVisit: 'October – March',
  budgetTier: 'mid',
  description: 'Majestic forts, royal palaces, desert sunsets, and camel safaris — the essence of Rajasthan royalty. Experience the vibrant culture, colorful markets, and timeless heritage of the Land of Kings.',
  pickupPricing: { pathankot: 24000, amritsar: 24000, chandigarh: 22000, delhiNCR: 21000 },
  itinerary: [
  { day: 1, title: 'Delhi / Jaipur – Arrival', description: 'Arrive at Jaipur. Check in at hotel. Evening visit to Chokhi Dhani for Rajasthani cultural experience.', distance: 'Local', travelTime: '1 hour', overnight: 'Jaipur' },
  { day: 2, title: 'Jaipur Sightseeing', description: 'Visit Amber Fort, City Palace, Hawa Mahal, Jantar Mantar, and Jal Mahal.', distance: '50 km', travelTime: '7–8 hours', sightseeing: ['Amber Fort', 'City Palace', 'Hawa Mahal', 'Jantar Mantar', 'Jal Mahal'], overnight: 'Jaipur' },
  { day: 3, title: 'Jaipur to Jodhpur', description: 'Drive to Jodhpur (340 km). Check in. Evening visit to Clock Tower market.', distance: '340 km', travelTime: '5–6 hours', overnight: 'Jodhpur' },
  { day: 4, title: 'Jodhpur Sightseeing', description: 'Visit Mehrangarh Fort, Jaswant Thada, Umaid Bhawan Palace, and Mandore Gardens.', distance: '40 km', travelTime: '6–7 hours', sightseeing: ['Mehrangarh Fort', 'Jaswant Thada', 'Umaid Bhawan Palace', 'Mandore Gardens'], overnight: 'Jodhpur' },
  { day: 5, title: 'Jodhpur to Jaisalmer', description: 'Drive to Jaisalmer (285 km). Check in. Evening visit to Gadisar Lake.', distance: '285 km', travelTime: '5–6 hours', overnight: 'Jaisalmer' },
  { day: 6, title: 'Jaisalmer Fort & Desert Safari', description: 'Visit Jaisalmer Fort, Patwon Ki Haveli, and Salim Singh Ki Haveli. Evening camel safari at Sam Sand Dunes. Desert camp overnight.', distance: '60 km', travelTime: '6–7 hours', sightseeing: ['Jaisalmer Fort', 'Patwon Ki Haveli', 'Sam Sand Dunes', 'Camel Safari'], overnight: 'Desert Camp' },
  { day: 7, title: 'Jaisalmer to Udaipur', description: 'Drive to Udaipur (500 km). Check in at hotel. Evening boat ride on Lake Pichola.', distance: '500 km', travelTime: '8–9 hours', overnight: 'Udaipur' },
  { day: 8, title: 'Udaipur Sightseeing – Departure', description: 'Visit City Palace, Jagdish Temple, Saheliyon Ki Bari, and Fateh Sagar Lake. Departure.', distance: '40 km', travelTime: '5–6 hours', sightseeing: ['City Palace', 'Jagdish Temple', 'Saheliyon Ki Bari', 'Fateh Sagar Lake'], overnight: 'Drop at Udaipur' }],

  vehicleDetails: 'Innova / SUV recommended for Rajasthan. All AC vehicles with experienced drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is Rajasthan good in summer?', a: 'Rajasthan is very hot in summer (April–June). October–March is the ideal time to visit.' },
  { q: 'Is camel safari included?', a: 'Camel safari charges are extra (approx ₹500–₹1,500 per person).' }]

},

// ─── GOLDEN TRIANGLE ─────────────────────────────────────────────────────────
{
  id: 'pkg-golden-triangle-6d',
  name: 'Golden Triangle Tour',
  destination: 'Delhi → Agra → Jaipur',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_118e7e561-1772252912875.png",
  alt: 'Taj Mahal Agra at sunrise with reflecting pool and symmetrical gardens in foreground',
  duration: '6D / 5N',
  durationDays: 6,
  durationNights: 5,
  rating: 4.7,
  reviews: 534,
  price: 14999,
  originalPrice: 19000,
  discount: 21,
  category: 'Golden Triangle',
  travelType: ['Family', 'Couple', 'Honeymoon', 'Group'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Taj Mahal', 'Agra Fort', 'Amber Fort', 'Qutub Minar', 'India Gate', 'Hawa Mahal'],
  tag: 'Classic Tour',
  tagColor: 'badge-primary',
  season: ['Winter', 'Autumn', 'Spring'],
  bestTimeToVisit: 'October – March',
  budgetTier: 'mid',
  description: 'India\'s most iconic tourist circuit — the Golden Triangle covers Delhi, Agra, and Jaipur. See the Taj Mahal, Agra Fort, Amber Fort, and the best of Mughal and Rajput heritage.',
  pickupPricing: { pathankot: 17999, amritsar: 17999, chandigarh: 15999, delhiNCR: 14999 },
  itinerary: [
  { day: 1, title: 'Delhi Sightseeing', description: 'Visit India Gate, Qutub Minar, Humayun\'s Tomb, Lotus Temple, and Red Fort.', distance: '60 km', travelTime: '7–8 hours', sightseeing: ['India Gate', 'Qutub Minar', 'Humayun Tomb', 'Red Fort', 'Lotus Temple'], overnight: 'Delhi' },
  { day: 2, title: 'Delhi to Agra', description: 'Drive to Agra (200 km). Visit Taj Mahal at sunset. Check in at hotel.', distance: '200 km', travelTime: '4–5 hours', sightseeing: ['Taj Mahal (Sunset)', 'Agra Fort'], overnight: 'Agra' },
  { day: 3, title: 'Agra Sightseeing & Fatehpur Sikri', description: 'Sunrise at Taj Mahal. Visit Agra Fort and Fatehpur Sikri. Drive to Jaipur.', distance: '240 km', travelTime: '5–6 hours', sightseeing: ['Taj Mahal (Sunrise)', 'Fatehpur Sikri', 'Itmad-ud-Daulah'], overnight: 'Jaipur' },
  { day: 4, title: 'Jaipur Sightseeing', description: 'Visit Amber Fort, City Palace, Hawa Mahal, and Jantar Mantar.', distance: '50 km', travelTime: '7–8 hours', sightseeing: ['Amber Fort', 'City Palace', 'Hawa Mahal', 'Jantar Mantar'], overnight: 'Jaipur' },
  { day: 5, title: 'Jaipur – Nahargarh Fort & Shopping', description: 'Visit Nahargarh Fort, Jal Mahal, and Johari Bazaar for shopping.', distance: '30 km', travelTime: '5–6 hours', sightseeing: ['Nahargarh Fort', 'Jal Mahal', 'Johari Bazaar'], overnight: 'Jaipur' },
  { day: 6, title: 'Jaipur to Delhi – Departure', description: 'Drive back to Delhi. Trip ends.', distance: '280 km', travelTime: '5–6 hours', overnight: 'Drop at Delhi' }],

  vehicleDetails: 'Innova / SUV / Sedan. All AC vehicles with experienced drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is Taj Mahal closed on Fridays?', a: 'Yes, Taj Mahal is closed on Fridays. We plan the itinerary accordingly.' },
  { q: 'What is the entry fee for Taj Mahal?', a: 'Entry fee is ₹50 for Indian nationals and ₹1,100 for foreign nationals (subject to change).' }]

},

// ─── AMRITSAR ────────────────────────────────────────────────────────────────
{
  id: 'pkg-amritsar-2d',
  name: 'Amritsar Golden Temple & Wagah Border Tour',
  destination: 'Amritsar, Punjab',
  image: "https://images.unsplash.com/photo-1689141083335-fc7aa682b166",
  alt: 'Golden Temple Amritsar illuminated at night with reflection in sacred sarovar pool',
  duration: '2D / 1N',
  durationDays: 2,
  durationNights: 1,
  rating: 4.9,
  reviews: 678,
  price: 3999,
  originalPrice: 5500,
  discount: 27,
  category: 'Amritsar',
  travelType: ['Family', 'Pilgrimage', 'Group', 'Couple'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Entry Tickets', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Golden Temple', 'Wagah Border Ceremony', 'Jallianwala Bagh', 'Durgiana Temple', 'Amritsar Food Trail'],
  tag: 'Must Visit',
  tagColor: 'badge-primary',
  season: ['Winter', 'Spring', 'Summer', 'Autumn'],
  bestTimeToVisit: 'October – March',
  budgetTier: 'budget',
  description: 'Visit the most sacred Sikh shrine — the Golden Temple — and witness the electrifying Wagah Border ceremony. Amritsar is a city of faith, history, and incredible food.',
  pickupPricing: { pathankot: 4999, amritsar: 3999, chandigarh: 6999, delhiNCR: 9999 },
  itinerary: [
  { day: 1, title: 'Amritsar Arrival & Golden Temple', description: 'Arrive at Amritsar. Check in at hotel. Visit Golden Temple for evening prayers and langar. Night view of the illuminated Golden Temple.', distance: '20 km local', travelTime: '4–5 hours', sightseeing: ['Golden Temple', 'Akal Takht', 'Langar Hall'], overnight: 'Amritsar' },
  { day: 2, title: 'Jallianwala Bagh & Wagah Border', description: 'Morning visit to Jallianwala Bagh and Durgiana Temple. Afternoon drive to Wagah Border (28 km) for the Beating Retreat ceremony. Return to Amritsar.', distance: '56 km', travelTime: '5–6 hours', sightseeing: ['Jallianwala Bagh', 'Durgiana Temple', 'Wagah Border Ceremony', 'Partition Museum'], overnight: 'Drop at Amritsar' }],

  vehicleDetails: 'Hatchback / Sedan / SUV. All AC vehicles with experienced drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'What time is the Wagah Border ceremony?', a: 'The Wagah Border ceremony takes place at sunset, approximately 5:15 PM in winter and 6:30 PM in summer.' },
  { q: 'Is the Golden Temple open 24 hours?', a: 'Yes, the Golden Temple is open 24 hours, 365 days a year.' }]

},

// ─── VAISHNO DEVI ─────────────────────────────────────────────────────────────
{
  id: 'pkg-vaishno-devi-3d',
  name: 'Vaishno Devi Yatra Package',
  destination: 'Pathankot → Katra → Vaishno Devi, Jammu & Kashmir',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa7b6bb8-1784787122605.png",
  alt: 'Vaishno Devi shrine cave entrance with devotees and Trikuta mountain range in background',
  duration: '3D / 2N',
  durationDays: 3,
  durationNights: 2,
  rating: 4.8,
  reviews: 567,
  price: 6999,
  originalPrice: 9000,
  discount: 22,
  category: 'Vaishno Devi',
  travelType: ['Pilgrimage', 'Family', 'Group'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Hotel', 'Meals', 'Parking Charges', 'Pony/Palki Charges', 'Battery Car Charges', 'Personal Expenses', 'Travel Insurance'],
  highlights: ['Vaishno Devi Shrine', 'Bhairon Temple', 'Ardhkuwari Cave', 'Katra Market', 'Shiv Khori Temple'],
  tag: 'Pilgrimage',
  tagColor: 'badge-secondary',
  season: ['Spring', 'Summer', 'Autumn', 'Winter'],
  bestTimeToVisit: 'March – June, September – November',
  budgetTier: 'budget',
  description: 'One of the most visited pilgrimage sites in India — Vaishno Devi Mata Shrine in the Trikuta Mountains. A spiritually fulfilling journey that millions undertake every year.',
  pickupPricing: { pathankot: 6999, amritsar: 8999, chandigarh: 11999, delhiNCR: 15999 },
  itinerary: [
  { day: 1, title: 'Pathankot to Katra', description: 'Depart from Pathankot. Drive to Katra (110 km). Check in at hotel. Register for Yatra Parchi. Evening visit to Katra market.', distance: '110 km', travelTime: '3–4 hours', sightseeing: ['Katra Market', 'Banganga'], overnight: 'Katra' },
  { day: 2, title: 'Vaishno Devi Darshan', description: 'Early morning start for the 14 km trek to Vaishno Devi Shrine. Visit Ardhkuwari Cave en route. Take blessings at the holy shrine. Return trek to Katra.', distance: '28 km trek', travelTime: '8–10 hours', sightseeing: ['Ardhkuwari Cave', 'Vaishno Devi Shrine', 'Bhairon Temple'], overnight: 'Katra', notes: 'Pony, Palki, and Battery Car services available at extra cost.' },
  { day: 3, title: 'Katra to Pathankot – Departure', description: 'Morning visit to Shiv Khori Temple (optional, 80 km from Katra). Depart for Pathankot. Trip ends.', distance: '110 km', travelTime: '3–4 hours', overnight: 'Drop at Pathankot' }],

  vehicleDetails: 'Hatchback / Sedan / SUV / Innova. All AC vehicles with experienced drivers.',
  bookingPolicy: 'Minimum 20% advance required to confirm booking.',
  cancellationPolicy: '7+ days before: 90% refund. 3–7 days: 50% refund. Less than 3 days: No refund.',
  faqs: [
  { q: 'Is Yatra Parchi mandatory?', a: 'Yes, Yatra Parchi (registration) is mandatory for Vaishno Devi. We can help you register online.' },
  { q: 'How long is the trek to Vaishno Devi?', a: 'The trek is approximately 14 km one way from Katra. Pony, Palki, and Battery Car services are available.' }]

},

// ─── AIRPORT TRANSFERS ────────────────────────────────────────────────────────
{
  id: 'pkg-airport-transfer-pathankot',
  name: 'Pathankot Airport / Railway Transfer',
  destination: 'Pathankot ↔ Any Destination in HP / J&K',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10a6e57d7-1768080082309.png",
  alt: 'Airport taxi service with professional driver and clean sedan car at airport terminal',
  duration: 'One Way / Round Trip',
  durationDays: 1,
  durationNights: 0,
  rating: 4.8,
  reviews: 1234,
  price: 1499,
  originalPrice: 2000,
  discount: 25,
  category: 'Airport Transfers',
  travelType: ['Family', 'Couple', 'Group', 'Corporate'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Parking Charges', 'Personal Expenses', 'Waiting Charges (after free time)'],
  highlights: ['24×7 Available', 'Flight Tracking', 'Door-to-Door Pickup', 'Verified Drivers', 'Clean Vehicles'],
  tag: 'Quick Book',
  tagColor: 'badge-primary',
  season: ['Spring', 'Summer', 'Autumn', 'Winter'],
  bestTimeToVisit: 'Available Year Round',
  budgetTier: 'budget',
  description: 'Reliable, punctual, and comfortable airport and railway station transfers from Pathankot to all major destinations in Himachal Pradesh and Jammu & Kashmir. Available 24×7.',
  pickupPricing: { pathankot: 1499, amritsar: 2499, chandigarh: 4999, delhiNCR: 7999 },
  itinerary: [
  { day: 1, title: 'Airport / Railway Pickup & Drop', description: 'Driver arrives 15 minutes before your scheduled pickup time. Comfortable transfer to your destination. Flight tracking for airport pickups.', sightseeing: [], overnight: 'Drop at Destination' }],

  vehicleDetails: 'Hatchback (up to 4 pax) / Sedan (up to 4 pax) / SUV Ertiga (up to 6 pax) / Innova (up to 7 pax) / Tempo Traveller (up to 12 pax).',
  bookingPolicy: 'Advance booking recommended. Same-day bookings accepted subject to availability.',
  cancellationPolicy: '24+ hours before: Full refund. 12–24 hours: 50% refund. Less than 12 hours: No refund.',
  faqs: [
  { q: 'Do you track flights for airport pickups?', a: 'Yes, we track your flight and adjust pickup time accordingly at no extra charge.' },
  { q: 'What if my flight is delayed?', a: 'We track your flight. If delayed, your driver will wait. Waiting charges apply after 1 hour free waiting time.' }]

},

// ─── TAXI PACKAGES ────────────────────────────────────────────────────────────
{
  id: 'pkg-taxi-pathankot-manali',
  name: 'Pathankot to Manali Taxi',
  destination: 'Pathankot → Manali, Himachal Pradesh',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_14111ef0b-1772176047291.png',
  alt: 'Taxi driving on mountain road towards Manali with snow-capped Himalayan peaks in background',
  duration: 'One Way',
  durationDays: 1,
  durationNights: 0,
  rating: 4.8,
  reviews: 876,
  price: 7499,
  originalPrice: 9500,
  discount: 21,
  category: 'Taxi Packages',
  travelType: ['Family', 'Couple', 'Group', 'Adventure'],
  includes: ['Vehicle', 'Driver', 'Fuel', 'Toll Tax', 'State Tax'],
  excludes: ['Parking Charges', 'Personal Expenses', 'Waiting Charges (after free time)'],
  highlights: ['Experienced Hill Drivers', 'AC Vehicle', 'Door-to-Door Pickup', '24×7 Support', 'Verified Drivers'],
  tag: 'HOT DEAL',
  tagColor: 'badge-secondary',
  season: ['Spring', 'Summer', 'Autumn'],
  bestTimeToVisit: 'March – November',
  budgetTier: 'budget',
  description: 'Comfortable and reliable taxi service from Pathankot to Manali. Experienced hill drivers, well-maintained AC vehicles, and door-to-door pickup service.',
  pickupPricing: { pathankot: 7499, amritsar: 9499, chandigarh: 7499, delhiNCR: 12499 },
  itinerary: [
  { day: 1, title: 'Pathankot to Manali (One Way)', description: 'Depart from Pathankot. Drive to Manali via Mandi (280 km). Drop at your hotel/destination in Manali.', distance: '280 km', travelTime: '9–10 hours', overnight: 'Drop at Manali' }],

  vehicleDetails: 'Hatchback ₹7,499 / Sedan ₹8,999 / SUV Ertiga ₹11,999 / Innova ₹13,999. All AC vehicles.',
  bookingPolicy: 'Advance booking recommended. Minimum 20% advance to confirm.',
  cancellationPolicy: '24+ hours before: Full refund. 12–24 hours: 50% refund. Less than 12 hours: No refund.',
  faqs: [
  { q: 'How long does it take from Pathankot to Manali?', a: 'Approximately 9–10 hours (280 km) depending on traffic and road conditions.' },
  { q: 'Is AC available on mountain roads?', a: 'AC is available during normal driving. On steep hill climbs, AC may be temporarily switched off for engine safety.' }]

}];


export const CATEGORIES = [
'All',
'Manimahesh Yatra',
'Chamba',
'Dalhousie & Khajjiar',
'Dharamshala & McLeodganj',
'Bir Billing',
'Spiti Valley',
'Kinnaur',
'Shimla',
'Manali',
'Kashmir',
'Leh Ladakh',
'Uttarakhand',
'Rajasthan',
'Golden Triangle',
'Amritsar',
'Vaishno Devi',
'Airport Transfers',
'Taxi Packages'];


export const PICKUP_CITIES = [
{ key: 'pathankot', label: 'Pathankot' },
{ key: 'amritsar', label: 'Amritsar' },
{ key: 'chandigarh', label: 'Chandigarh' },
{ key: 'delhiNCR', label: 'Delhi NCR' }] as
const;

export type PickupCity = keyof PickupPricing;